import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, SignalIcon, SparklesIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { useSignalkDashboardData } from '../../hooks/useSignalkDashboardData';
import { createPowerReader, listPowerPaths } from '../../utils/powerFlow';
import { buildEngineModel, buildDemoEngineFlat, hasEngineData } from '../../utils/engineDiagnostics';
import {
  Page, Header, Title, Subtitle, Toolbar, Badge, Btn, Card,
  MetricGrid, MetricCard, MetricLabel, MetricValue, MetricSub,
} from '../liveboat/liveBoatStyles';

const Engines = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  align-items: start;
`;

const EngineCard = styled(Card)`
  padding: 1rem 1.1rem 1.1rem;
`;

const EngineHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.9rem;
`;

const EngineName = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
`;

const GroupTitle = styled.div`
  margin: 1rem 0 0.5rem;
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${theme.colors.text.secondary};
`;

const Tile = styled(MetricCard)`
  opacity: ${(p) => (p.$muted ? 0.55 : 1)};
  ${(p) => (p.$big ? 'grid-column: 1 / -1;' : '')}
`;

const BigValue = styled(MetricValue)`
  font-size: ${(p) => (p.$big ? '2.2rem' : '1.25rem')};
`;

const Unit = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 0.3rem;
  color: ${theme.colors.text.secondary};
`;

const Flags = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 0.4rem 0.8rem;
`;

const Flag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
  background: ${(p) => (p.$state === 'alarm' ? theme.colors.error.main : p.$state === 'ok' ? theme.colors.success.main : theme.colors.border.primary)};
`;

const Note = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  line-height: 1.45;
`;

const Banner = styled.div`
  margin-bottom: 1rem;
  padding: 0.7rem 1rem;
  border-radius: ${theme.borderRadius.md};
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
  color: ${theme.colors.warning.light};
  font-size: ${theme.fontSizes.sm};
`;

const CodesCard = styled(Card)`
  margin-top: 1rem;
  padding: 1rem 1.1rem;
`;


const HealthCard = styled(Card)`
  margin-bottom: 1rem;
  padding: 1rem 1.1rem;
`;

const HealthHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
`;

const HealthTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${theme.fontSizes.md};
  font-weight: ${theme.fontWeights.semibold};
`;

const Summary = styled.p`
  margin: 0 0 0.75rem;
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  color: ${theme.colors.text.primary};
`;

const FindingRow = styled.div`
  display: flex;
  gap: 0.6rem;
  padding: 0.5rem 0;
  border-top: 1px solid ${theme.colors.border.secondary};
  font-size: ${theme.fontSizes.sm};
  line-height: 1.4;
`;

const FindingTitle = styled.div`
  font-weight: ${theme.fontWeights.semibold};
`;

const Suggestions = styled.ul`
  margin: 0.5rem 0 0;
  padding-left: 1.1rem;
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
  color: ${theme.colors.text.primary};
`;

const Fine = styled.div`
  margin-top: 0.6rem;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
`;

const HEALTH_STATUS = {
  alert: { label: 'Needs attention now', bg: 'rgba(239,68,68,0.15)', color: theme.colors.error.light, border: 'rgba(239,68,68,0.4)' },
  attention: { label: 'Worth a look', bg: 'rgba(245,158,11,0.15)', color: theme.colors.warning.light, border: 'rgba(245,158,11,0.4)' },
  ok: { label: 'Looks normal', bg: 'rgba(16,185,129,0.15)', color: theme.colors.success.light, border: 'rgba(16,185,129,0.35)' },
  idle: { label: 'Engines off', bg: undefined, color: undefined, border: undefined },
  'no-data': { label: 'No engine data', bg: undefined, color: undefined, border: undefined },
};

const SEVERITY_DOT = { alarm: 'alarm', warning: 'alarm', info: 'unknown' };

function HealthCheck({ report, loading, error, isDemo, onRecheck }) {
  const st = report ? HEALTH_STATUS[report.status] || HEALTH_STATUS['no-data'] : null;
  const checked = report ? new Date(report.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
  return (
    <HealthCard>
      <HealthHead>
        <HealthTitle>
          <SparklesIcon className="w-5 h-5" style={{ color: theme.colors.secondary.light }} />
          Engine health check
          {st ? (
            <Badge $bg={st.bg} $color={st.color} $border={st.border}>
              {st.label}
            </Badge>
          ) : null}
        </HealthTitle>
        <Btn type="button" onClick={onRecheck} disabled={loading || isDemo}>
          <ArrowPathIcon className="w-4 h-4" />
          {loading ? 'Checking…' : 'Re-check'}
        </Btn>
      </HealthHead>

      {isDemo ? (
        <Note>The health check runs on your real engine data. It will appear here once your engines are connected.</Note>
      ) : error ? (
        <Note>{error}</Note>
      ) : !report ? (
        <Note>Checking your engines…</Note>
      ) : (
        <>
          {report.ai?.summary ? <Summary>{report.ai.summary}</Summary> : null}
          {!report.ai?.summary && report.status === 'ok' ? (
            <Summary>All readings are within normal range for the running engines.</Summary>
          ) : null}
          {report.status === 'idle' ? (
            <Summary>The engines are off, so there is nothing to check. Readings shown below are the last known values.</Summary>
          ) : null}
          {report.status === 'no-data' ? <Summary>No engine readings have been received for this vessel yet.</Summary> : null}

          {report.findings.map((f, i) => (
            <FindingRow key={`${f.id}-${i}`}>
              <Dot $state={SEVERITY_DOT[f.severity]} style={{ marginTop: '0.35rem' }} />
              <div>
                <FindingTitle>{f.title}</FindingTitle>
                <Note>{f.detail}</Note>
              </div>
            </FindingRow>
          ))}
          {(report.ai?.observations || []).map((o, i) => (
            <FindingRow key={`ai-${i}`}>
              <Dot $state={o.severity === 'warning' ? 'alarm' : 'unknown'} style={{ marginTop: '0.35rem' }} />
              <div>
                <FindingTitle>{o.engine ? `${o.engine} · AI observation` : 'AI observation'}</FindingTitle>
                <Note>{o.text}</Note>
              </div>
            </FindingRow>
          ))}
          {report.ai?.suggestions?.length ? (
            <Suggestions>
              {report.ai.suggestions.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </Suggestions>
          ) : null}

          <Fine>
            Checked {checked}.{' '}
            {report.ai ? 'Summary written by AI from your live readings. ' : ''}
            {report.aiError ? `${report.aiError}. ` : ''}
            {!report.aiAvailable ? 'AI summary is not enabled on this server. ' : ''}
            This is guidance, not a substitute for an inspection.
          </Fine>
        </>
      )}
    </HealthCard>
  );
}

const LINK_STYLE = {
  verified: { bg: 'rgba(16,185,129,0.15)', color: theme.colors.success.light, border: 'rgba(16,185,129,0.35)' },
  live: { bg: 'rgba(14,165,233,0.15)', color: theme.colors.secondary.light, border: 'rgba(14,165,233,0.35)' },
  'ecu-off': { bg: 'rgba(245,158,11,0.15)', color: theme.colors.warning.light, border: 'rgba(245,158,11,0.4)' },
  offline: { bg: 'rgba(239,68,68,0.12)', color: theme.colors.error.light, border: 'rgba(239,68,68,0.35)' },
  none: {},
};

const GROUPS = [
  { key: 'engine', title: 'Engine' },
  { key: 'sensors', title: 'Air, fuel and ignition (Fox Marine gateway)' },
];

function FieldTile({ f }) {
  if (!f.available) {
    return (
      <Tile $muted $big={f.big}>
        <MetricLabel>{f.label}</MetricLabel>
        <MetricValue>—</MetricValue>
        <MetricSub>{f.planned ? 'Not mapped yet' : f.bleOnly ? 'No Bluetooth data' : 'No data'}</MetricSub>
      </Tile>
    );
  }
  return (
    <Tile $big={f.big} $muted={f.stale}>
      <MetricLabel>{f.label}</MetricLabel>
      <BigValue $big={f.big}>
        {f.display}
        {f.unit ? <Unit>{f.unit}</Unit> : null}
      </BigValue>
      {f.sub ? <MetricSub>{f.sub}</MetricSub> : null}
      {f.stale ? <MetricSub>Last known value</MetricSub> : null}
    </Tile>
  );
}

function EnginePanel({ engine }) {
  const style = LINK_STYLE[engine.link.state] || {};
  return (
    <EngineCard>
      <EngineHead>
        <EngineName>{engine.label}</EngineName>
        <Badge $bg={style.bg} $color={style.color} $border={style.border} title={engine.link.text}>
          <SignalIcon className="w-3.5 h-3.5" />
          {engine.link.text}
        </Badge>
      </EngineHead>

      {GROUPS.map((g) => {
        const fields = engine.fields.filter((f) => f.group === g.key);
        return (
          <div key={g.key}>
            <GroupTitle>{g.title}</GroupTitle>
            <MetricGrid>
              {fields.map((f) => (
                <FieldTile key={f.key} f={f} />
              ))}
            </MetricGrid>
          </div>
        );
      })}

      <GroupTitle>Alarms and lamps</GroupTitle>
      {engine.flagsAvailable ? (
        <Flags>
          {engine.flags.map((f) => (
            <Flag key={f.key}>
              <Dot $state={f.state} />
              {f.label}
            </Flag>
          ))}
        </Flags>
      ) : (
        <Note>
          The gateway&apos;s alarm and lamp flags are not mapped yet, so nothing is shown here. This is not an
          &quot;all clear&quot;.
        </Note>
      )}
    </EngineCard>
  );
}

const EngineDiagnosticsPage = () => {
  const { vesselId: vesselIdParam } = useParams();
  const navigate = useNavigate();
  const paramId = vesselIdParam ? parseInt(vesselIdParam, 10) : null;

  const { vesselId, liveData, signalKFlat, vesselStreamConnected, loadSignalKData } = useSignalkDashboardData(paramId);
  const [vessel, setVessel] = useState(null);
  const [now, setNow] = useState(Date.now());
  const demoFlat = useMemo(() => buildDemoEngineFlat(), []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!vesselId) return;
    api
      .get(`/api/vessels/${vesselId}`)
      .then((r) => setVessel(r.data?.vessel || r.data))
      .catch(() => setVessel(null));
  }, [vesselId]);

  // Priority: live → database → demo sample (clearly labelled).
  const { model, isDemo } = useMemo(() => {
    const paths = listPowerPaths(liveData, signalKFlat);
    if (hasEngineData(paths)) {
      const read = createPowerReader({ liveData, signalKFlat, vesselStreamConnected, now });
      return { model: buildEngineModel(paths, read, { now }), isDemo: false };
    }
    const read = createPowerReader({ liveData: {}, signalKFlat: demoFlat, vesselStreamConnected: false, now });
    return { model: buildEngineModel(Object.keys(demoFlat), read, { now }), isDemo: true };
  }, [liveData, signalKFlat, vesselStreamConnected, now, demoFlat]);

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  const loadReport = useCallback(
    async (refresh = false) => {
      if (!vesselId) return;
      setReportLoading(true);
      try {
        const res = await api.get(`/api/vessels/${vesselId}/engine-diagnostics${refresh ? '?refresh=1' : ''}`);
        setReport(res.data);
        setReportError(null);
      } catch (err) {
        setReportError(err.response?.data?.error || 'Could not run the engine health check right now.');
      } finally {
        setReportLoading(false);
      }
    },
    [vesselId]
  );

  // Run once the real data is known (not for the demo sample), then every 3 minutes while the page is open.
  useEffect(() => {
    if (!vesselId || isDemo) return undefined;
    loadReport(false);
    const t = setInterval(() => loadReport(false), 3 * 60 * 1000);
    return () => clearInterval(t);
  }, [vesselId, isDemo, loadReport]);

  const sourceBadge = isDemo
    ? { label: 'Demo data', bg: 'rgba(245,158,11,0.15)', color: theme.colors.warning.light, border: 'rgba(245,158,11,0.4)' }
    : model.source === 'live'
      ? { label: 'Live', bg: 'rgba(16,185,129,0.15)', color: theme.colors.success.light, border: 'rgba(16,185,129,0.35)' }
      : model.source === 'stale'
        ? { label: 'Stale', bg: 'rgba(245,158,11,0.15)', color: theme.colors.warning.light, border: 'rgba(245,158,11,0.4)' }
        : { label: 'Last known (database)', bg: 'rgba(239,68,68,0.12)', color: theme.colors.error.light, border: 'rgba(239,68,68,0.35)' };

  const lastUpdate = model.lastUpdate
    ? new Date(model.lastUpdate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <Page>
      <Header>
        <div>
          <Btn type="button" onClick={() => navigate(-1)} style={{ marginBottom: '0.5rem' }}>
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </Btn>
          <Title>
            <WrenchScrewdriverIcon className="w-6 h-6" style={{ color: theme.colors.secondary.light }} />
            Engine Diagnostics
          </Title>
          <Subtitle>
            NMEA 2000 and Fox Marine gateway data for {vessel?.vessel_name || 'your vessel'}
            {lastUpdate ? ` · last reading ${lastUpdate}` : ''}
          </Subtitle>
        </div>
        <Toolbar>
          <Badge $bg={sourceBadge.bg} $color={sourceBadge.color} $border={sourceBadge.border}>
            <SignalIcon className="w-3.5 h-3.5" />
            {sourceBadge.label}
          </Badge>
          <Btn type="button" onClick={() => loadSignalKData()}>
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </Btn>
        </Toolbar>
      </Header>

      {isDemo ? (
        <Banner>
          No engine data from this vessel yet — showing a demo sample. Connect your engines through the BoatWise
          SignalK plugin (and the Fox Marine plugin for gateway data) to see your real engines.
        </Banner>
      ) : null}

      <HealthCheck
        report={report}
        loading={reportLoading}
        error={reportError}
        isDemo={isDemo}
        onRecheck={() => loadReport(true)}
      />

      <Engines>
        {model.engines.map((engine) => (
          <EnginePanel key={engine.id} engine={engine} />
        ))}
      </Engines>

      <CodesCard>
        <GroupTitle style={{ marginTop: 0 }}>Fault codes</GroupTitle>
        <Note>
          Reading and clearing stored fault codes is not available yet. When it is added, codes will be listed here
          for each engine, and clearing will ask for confirmation and be recorded in an audit log.
        </Note>
      </CodesCard>
    </Page>
  );
};

export default EngineDiagnosticsPage;
